import React, { useEffect, useState } from "react";
import { Modal, Typography, Space, Divider } from "antd";
import { IoClose } from "react-icons/io5";
import SearchInput from "../Forms/SearchInput";
import DataTable from "../Tables/Datatable";
import { useApiCall } from "../../services/api";
import { useSelector } from "react-redux";
import PrimaryButton from "../Forms/PrimaryButton";
import { useGlobalModal } from "../../context/SuccessModalProvider";
import { PERMISSION_IDS } from "../../constant";

const { Title } = Typography;

export default function SelectClientModal({ isOpen, setIsOpen, title = "Select Client", description, selectedClient, onSubmit }) {
  const { postApi, loading } = useApiCall();
  const [allClient, setAllClient] = useState([]);
  const [filteredClient, setFilteredClient] = useState([]);
  const [selectedRowKey, setSelectedRowKey] = useState(null);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [loader, setLoader] = useState(false);
  const { user_id, is_sales_person, is_agent, customer_id, permission_ids } = useSelector((state) => state.auth);
  const { showPopup } = useGlobalModal();

  // Fetch clients when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchClients();
      setSearchText("");
    }
  }, [isOpen]);

  const fetchClients = async () => {
    try {
      const payload = {
        ...(is_sales_person && !permission_ids.includes(PERMISSION_IDS.UNIVERSAL_ORDER_MANAGEMENT) && { user_id }),
        ...(is_agent && { agent_customer_id: customer_id }),
      };

      const response = await postApi(is_agent ? "sales/agent/agent-customer-list" : "sales/assign-customer-list", payload); // ✅ replace with your endpoint/payload
      const formatted = (response.data || []).map((item, index) => ({
        ...item,
        key: index + 1,
      }));
      setAllClient(formatted);
      setFilteredClient(formatted);

      // Preselect if `selectedClient` exists
      if (selectedClient) {
        const match = formatted.find((c) => c.company_name === selectedClient?.company_name);
        if (match) {
          setSelectedRowKey(match.key);
          setSelectedRowData(match);
        }
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  // Handle search (filters from allClient)
  const handleSearchChange = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchText(value);

    const filtered = allClient.filter((c) => c.company_name?.toLowerCase().includes(value));
    setFilteredClient(filtered);
  };

  // Table columns
  const columns = [
    {
      title: "#",
      key: "index",
      render: (_, __, index) => index + 1,
      width: 60,
    },
    {
      title: "Client Name",
      dataIndex: "company_name",
      key: "company_name",
    },
  ];

  const rowSelection = {
    type: "radio",
    columnWidth: 0,
    renderCell: () => null,
    selectedRowKeys: selectedRowKey ? [selectedRowKey] : [],
    onChange: (keys, rows) => {
      setSelectedRowKey(keys[0]);
      setSelectedRowData(rows[0]);
    },
  };

  return (
    <Modal className="form-css-2 custom-modal select-client-modal" open={isOpen} footer={null} onCancel={() => setIsOpen(false)}>
      {/* Header */}
      <div className="flex justify-between items-center ">
        <Title level={2} className="text-3xl font-semibold text-gray-800 font-abhaya m-0">
          {title}
        </Title>
      </div>

      {/* Description */}
      {description && (
        <div className="text-left">
          <p className="text-gray-500 text-sm pb-2">{description}</p>
        </div>
      )}

      {/* Search */}
      <SearchInput value={searchText} onChange={handleSearchChange} placeholder="Search for clients" />

      {/* Table */}
      <div className="mt-6 space-y-3">
        <div className="w-full px-4 text-left flex">
          <span className="w-[25%] text-left font-medium text-gray-700">#</span>
          <span className="text-gray-900 text-left w-[75%]">Client Name</span>
        </div>
        <Divider />
        {filteredClient?.map((item, i) => {
          const isSelected = selectedRowKey === item.key;
          return (
            <div
              key={i}
              onClick={() => {
                setSelectedRowKey(item.key);
                setSelectedRowData(item);
              }}
              className={`flex justify-between items-center px-4 py-2 cursor-pointer hover:bg-[#FBEEEC] hover:font-semibold rounded-md ${
                isSelected ? "bg-[#FBEEEC]" : "bg-transparent"
              }`}
            >
              <div className="w-full text-left flex justify-between">
                <span className="font-medium text-gray-700">{i + 1}</span>
                <span className="text-gray-900 text-left w-1/2">{item.company_name}</span>
                <span></span>
              </div>
            </div>
          );
        })}
        {filteredClient?.length === 0 && <div className="py-2 text-center text-gray-500">Sorry, no clients found</div>}
      </div>

      {/* Footer */}
      <div className="w-full mt-4 flex justify-center">
        <PrimaryButton
          className="me-[30px]"
          text="Proceed"
          onClick={() => {
            if (!selectedRowData)
              return showPopup({
                title: null,
                content: <span className="custom-modal-title">Please select a client.</span>,
                onOk: () => console.log("Confirmed!"),
                className: "custom-success-modal",
              });

            setLoader(true);
            setTimeout(() => {
              onSubmit(selectedRowData);
              setLoader(false);
            }, 300);
          }}
          loading={loading || loader}
        />
      </div>
    </Modal>
  );
}
